<template>
    <div class="ht-wrap" :class="{'ht-disabled': disabled, 'ht-focused': focused}">
        <label v-if="label" class="ht-label">{{ label }}</label>
        <div
            ref="editor"
            class="ht-editor"
            :contenteditable="disabled ? 'false' : 'true'"
            :data-placeholder="placeholder"
            :style="{ minHeight }"
            spellcheck="true"
            @input="onInput"
            @paste="onPaste"
            @keydown="onKeydown"
            @focus="focused = true"
            @blur="focused = false"
        ></div>
        <div v-if="hint" class="ht-hint">{{ hint }}</div>
    </div>
</template>

<script>
const PLACEHOLDER_PATTERN = /\[[^\]\n]+\]/g;

function escapeHtml(s){
    return String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function wrapPlaceholders(html){
    return html.replace(PLACEHOLDER_PATTERN, m => `<span class="ht-mark">${m}</span>`);
}

function templateToHtml(template){
    const trimmed = (template || '').replace(/^\n+/, '').replace(/\n+$/, '');
    const escaped = escapeHtml(trimmed);
    const withBold = escaped
        .replace(/&lt;b&gt;/g, '<b contenteditable="false" class="ht-bold">')
        .replace(/&lt;\/b&gt;/g, '</b>');
    const withMarks = wrapPlaceholders(withBold);
    return withMarks.replace(/\n/g, '<br>');
}

function plainToHtml(value){
    const escaped = escapeHtml(value || '');
    return wrapPlaceholders(escaped).replace(/\n/g, '<br>');
}

export default {
    props: {
        value: { type: String, default: '' },
        label: { type: String, default: '' },
        placeholder: { type: String, default: '' },
        hint: { type: String, default: '' },
        disabled: { type: Boolean, default: false },
        minRows: { type: [Number, String], default: 6 },
    },
    data(){
        return { focused: false };
    },
    computed: {
        minHeight(){
            return `${Number(this.minRows) * 1.5}em`;
        },
    },
    mounted(){
        if (this.value){
            this.setPlainValue(this.value);
        }
    },
    methods: {
        setRichTemplate(template){
            const el = this.$refs.editor;
            if (!el) return;
            el.innerHTML = templateToHtml(template);
            this.emitValue();
        },
        setPlainValue(text){
            const el = this.$refs.editor;
            if (!el) return;
            el.innerHTML = plainToHtml(text);
            this.emitValue();
        },
        clear(){
            const el = this.$refs.editor;
            if (!el) return;
            el.innerHTML = '';
            this.emitValue();
        },
        getPlainText(){
            const el = this.$refs.editor;
            if (!el) return '';
            return el.innerText.replace(/\u00A0/g, ' ');
        },
        emitValue(){
            this.$emit('input', this.getPlainText());
        },
        refreshPlaceholderHighlights(){
            const el = this.$refs.editor;
            if (!el) return;
            const marks = el.querySelectorAll('.ht-mark');
            marks.forEach(m => {
                const text = m.textContent || '';
                const matches = text.match(/^\[[^\]\n]+\]$/);
                if (!matches){
                    const parent = m.parentNode;
                    if (!parent) return;
                    while (m.firstChild){
                        parent.insertBefore(m.firstChild, m);
                    }
                    parent.removeChild(m);
                }
            });
        },
        onInput(){
            this.refreshPlaceholderHighlights();
            this.emitValue();
        },
        onPaste(e){
            e.preventDefault();
            const text = (e.clipboardData || window.clipboardData).getData('text/plain') || '';
            if (document.queryCommandSupported && document.queryCommandSupported('insertText')){
                document.execCommand('insertText', false, text);
            } else {
                const sel = window.getSelection();
                if (!sel || !sel.rangeCount) return;
                const range = sel.getRangeAt(0);
                range.deleteContents();
                range.insertNode(document.createTextNode(text));
                range.collapse(false);
                sel.removeAllRanges();
                sel.addRange(range);
            }
            this.refreshPlaceholderHighlights();
            this.emitValue();
        },
        onKeydown(e){
            if ((e.ctrlKey || e.metaKey) && /^(b|i|u)$/i.test(e.key)){
                e.preventDefault();
            }
        },
    },
    watch: {
        value(newVal){
            const current = this.getPlainText();
            if (newVal === current) return;
            if (!newVal){
                this.clear();
            }
        },
        disabled(){
            // Re-render to apply contenteditable change cleanly when toggled mid-edit
            const current = this.getPlainText();
            if (this.$refs.editor){
                this.$refs.editor.innerHTML = plainToHtml(current);
            }
        },
    },
};
</script>

<style scoped>
.ht-wrap {
    display: block;
    width: 100%;
}
.ht-label {
    display: block;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 6px;
    font-weight: 500;
}
.ht-editor {
    width: 100%;
    padding: 12px 14px;
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    background: #fff;
    font: inherit;
    font-size: 14px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    color: rgba(0, 0, 0, 0.87);
    caret-color: var(--v-primary-base, #1976d2);
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.ht-focused .ht-editor {
    border-color: var(--v-primary-base, #1976d2);
    box-shadow: inset 0 0 0 1px var(--v-primary-base, #1976d2);
}
.ht-disabled .ht-editor {
    background: rgba(0, 0, 0, 0.04);
    color: rgba(0, 0, 0, 0.38);
    cursor: not-allowed;
}
.ht-editor:empty:before {
    content: attr(data-placeholder);
    color: rgba(0, 0, 0, 0.38);
    pointer-events: none;
}
.ht-editor >>> .ht-bold {
    font-weight: 700;
    user-select: text;
}
.ht-editor >>> .ht-mark {
    background-color: #eee;
    border-radius: 3px;
    /* box-shadow: inset 0 0 0 1px #f59e0b; */
    padding: 0 1px;
}
.ht-hint {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    margin-top: 6px;
    line-height: 1.4;
}
</style>
